'use client'

import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { CurrencyInput } from './CurrencyInput'

interface EditUsageLimitPopoverProps {
  currentBudget: number | null
  onSaved: () => void | Promise<void>
  trigger: React.ReactNode
}

export function EditUsageLimitPopover({
  currentBudget,
  onSaved,
  trigger,
}: EditUsageLimitPopoverProps) {
  const [open, setOpen] = useState(false)
  const [valueCents, setValueCents] = useState(0)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    // Display still receives the stored credit budget. If credits_per_dollar
    // changes, this can drift until the API returns a computed USD-cent value.
    setValueCents(currentBudget ?? 0)
  }, [currentBudget, open])

  const handleSave = async () => {
    if (!Number.isFinite(valueCents) || valueCents < 0) {
      toast.error('Enter a valid usage limit.')
      return
    }

    setSaving(true)
    try {
      const res = await fetch('/api/wallet/usage-limits', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        // Send USD cents; the API server-side converts using the live
        // credits_per_dollar rate. Audit finding #14.
        body: JSON.stringify({ monthly_budget_cents: valueCents }),
      })
      const payload = await res.json().catch(() => ({})) as { error?: string }
      if (!res.ok) throw new Error(payload.error || 'Failed to save usage limit')

      await onSaved()
      setOpen(false)
      toast.success('Usage limit updated')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save usage limit')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 border-atrium-border bg-white p-4 font-sans dark:bg-atrium-inner"
      >
        <div>
          <h3 className="text-lg font-medium text-atrium-black">Edit usage limit</h3>
          <p className="mt-1 text-sm text-atrium-muted">
            Set a monthly warning threshold for platform AI events.
          </p>
        </div>

        <div className="mt-4">
          <label htmlFor="usage-limit-usd" className="text-sm font-medium text-atrium-black">
            Monthly limit (USD)
          </label>
          <CurrencyInput
            id="usage-limit-usd"
            valueCents={valueCents}
            onChangeCents={setValueCents}
            minCents={0}
            disabled={saving}
            placeholder="No limit"
          />
          <p className="mt-2 text-xs text-atrium-muted">
            Warning threshold. AI events continue charging from wallet balance after this limit.
          </p>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
